import { FunctionComponent, SyntheticEvent, memo, useEffect } from 'react';
import { useKey } from 'react-use';
import { InputNumber } from 'antd';
import _ from 'lodash';
import { useProfile } from '../../components/profile/hooks';
import playAudio from '~frontend/utils/playAudio';
import {
  useItemsLeft,
  useUpgradeAmount,
  useIdsItemsLeft,
  useMode,
  useStatusGame,
  useItemRight,
  useLine,
} from './hooks';

import { SteamItemImage } from '../item';
import Currency from '../Currency';
import { number, Price, convertNumber } from '~ui/Price';
import { useConfig } from '~components/hooks';
import MoreItems from './MoreItems';

const coords = {
  x: 0,
};

const SliderMoney: FunctionComponent<{ active: boolean }> = memo(
  ({ active }: { active: boolean }) => {
    const [line, setLine] = useLine();
    const [itemRight] = useItemRight();
    const [statusGame] = useStatusGame();
    const getProfile = useProfile();
    const [upgradeAmount, setUpgradeAmount] = useUpgradeAmount();
    const minPrice = _.toInteger(itemRight?.price / 15);

    useKey(
      'ArrowLeft',
      () => {
        if (!active) {
          return false;
        }

        setUpgradeAmount(upgradeAmount =>
          upgradeAmount < 1 ? 0 : upgradeAmount - 1,
        );
      },
      undefined,
      [active],
    );

    useKey(
      'ArrowRight',
      () => {
        if (!active) {
          return false;
        }

        setUpgradeAmount(upgradeAmount =>
          upgradeAmount >= getProfile?.money
            ? getProfile?.money
            : upgradeAmount + 1,
        );
      },
      undefined,
      [active],
    );

    const startSlide = (event: SyntheticEvent) => {
      if (!active) {
        return false;
      }

      coords.x = (event.nativeEvent as MouseEvent).pageX;
      document.addEventListener('mousemove', slideMove, false);
      document.addEventListener('mouseup', stopSlide, false);
    };

    const stopSlide = () => {
      if (!active) {
        return false;
      }

      document.removeEventListener('mousemove', slideMove, false);
      document.removeEventListener('mouseup', stopSlide, false);
    };

    const slideMove = (event: MouseEvent) => {
      if (!active) {
        return false;
      }

      if (statusGame === 2) {
        return null;
      }

      const delta = event.pageX - coords.x;
      const fullWidth = 168;

      if (delta === 0) {
        return;
      }

      let deltaMoney = (delta / fullWidth) * getProfile?.money;

      if (delta < 0) {
        deltaMoney = upgradeAmount + (delta / fullWidth) * getProfile?.money;
      } else if (upgradeAmount !== 0) {
        deltaMoney += upgradeAmount;
      }

      if (deltaMoney < minPrice) {
        return setUpgradeAmount(
          minPrice > getProfile?.money ? getProfile?.money : minPrice,
        );
      }

      if (deltaMoney > getProfile?.money) {
        return setUpgradeAmount(getProfile?.money);
      } else if (deltaMoney < 0) {
        return setUpgradeAmount(0);
      }

      setUpgradeAmount(_.toInteger(deltaMoney));
    };

    const onChange = (value: number) => {
      setUpgradeAmount(
        number(value) > getProfile?.money ? getProfile?.money : number(value),
      );
    };

    useEffect(() => {
      setLine((upgradeAmount * 100) / getProfile?.money);
    }, [upgradeAmount]);

    return (
      <div className={`upgrade-money ${active && 'active'}`}>
        <div className="upgrade-money-wrap">
          <div className="upgrade-money-content">
            <div className="money-title">
              <div className="icon material-icons" data-icon="payments"></div>
            </div>
            <div className="cost">
              <InputNumber
                onChange={onChange}
                value={upgradeAmount}
                min={minPrice}
                step={1}
                max={getProfile?.money}
                formatter={value => convertNumber(value).toFixed(0)}
                controls={false}
              />
              <Currency />
            </div>
            <div className="progress">
              <div
                className="progress-line"
                style={{
                  width: `${line}%`,
                }}
              >
                <div
                  className="progress-line-icon"
                  style={{ cursor: 'pointer' }}
                  onMouseDown={startSlide}
                ></div>
              </div>
            </div>
            <div className="price-min-max">
              <div className="price-min-max-val">
                0 <Currency />
              </div>
              <div className="price-min-max-val">
                {convertNumber(getProfile?.money).toFixed(0)} <Currency />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

function UpgradeLeft() {
  const [mode] = useMode();
  const [itemsLeft, setItemsLeft] = useItemsLeft();
  const [upgradeAmount, setUpgradeAmount] = useUpgradeAmount();
  const [idsItemsLeft, setIdsItemsLeft] = useIdsItemsLeft();
  const [statusGame, setStatusGame] = useStatusGame();
  const getConfig = useConfig();

  const removeItemFromUpgrade = (event: SyntheticEvent<HTMLElement>) => {
    event.preventDefault();

    if (statusGame === 2) {
      return null;
    }

    const gameId = Number(event.currentTarget.dataset.id);

    const getGame = _.clone(_.find(itemsLeft, ['id', gameId]));

    setIdsItemsLeft(_.filter(idsItemsLeft, getItem => getItem !== gameId));
    setItemsLeft(_.filter(itemsLeft, getItem => getItem.id !== gameId));

    setUpgradeAmount(upgradeAmount - getGame.price);
    setStatusGame(0);

    playAudio(null, '14.mp3', 0.4);
  };

  const getGame = _.first(itemsLeft);

  return (
    <div
      className={`upgrade-main-left ${getGame && 'active'} ${
        mode === 1 && 'active'
      }`}
    >
      <div className="item">
        {_.size(itemsLeft) > 1 ? (
          <MoreItems />
        ) : (
          <>
            {' '}
            <SliderMoney active={mode === 1} />
            {getGame && (
              <div
                className="in-case-item status-1"
                onClick={removeItemFromUpgrade}
                data-id={getGame.id}
              >
                <a className="close" href="#">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                  >
                    <circle cx="10" cy="10" r="10" fill="#750881"></circle>
                    <path
                      d="M2211,75.713l-4.3,4.3-.717-.715,4.3-4.3-4.3-4.3.717-.716,4.3,4.3,4.3-4.3.716.716-4.3,4.3,4.3,4.3-.716.715Z"
                      transform="translate(-2201 -65)"
                      fill="#f0a1ff"
                    ></path>
                  </svg>
                </a>
                <div
                  className="in-case-item active status-1"
                  data-rar={getGame.getItem.color}
                  onMouseEnter={playAudio}
                  data-audio="5.wav"
                  data-volume="0.4"
                >
                  {itemsLeft.length === 1 && (
                    <div className="in-case-cost">
                      {getConfig.isEU ? (
                        <>
                          <Currency />
                          <Price sum={getGame.price} />
                        </>
                      ) : (
                        <>
                          <Price sum={getGame.price} />
                          <Currency />
                        </>
                      )}
                    </div>
                  )}
                  <div className="in-case-item-wrap">
                    <div className="img">
                      <div className="in-case-item-bg" />
                      <SteamItemImage
                        url={getGame.getItem.getImage}
                        size="350x350"
                      />
                    </div>
                    <div className="in-case-info">
                      <div className="in-case-item-name">
                        {getGame.getItem.getName}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default UpgradeLeft;
